import { defineComponent as _defineComponent } from 'vue'
import { computed, reactive, watch } from 'vue';
import CInput from '@/components/shared/CInput';
import CCheckbox from '@/components/shared/CCheckbox.vue';
import CButton from '@/components/shared/CButton';
import CSelect, { valueReducer, type CSelectItem } from '@/components/shared/CSelect';
import type {
    BaseCanvasElement,
    BaseTextElement,
    BoundedTextElement,
    PathITextElement,
    TextTransformElement,
} from '@/typings/customilyElements/canvasElements';
import type { CanvasEx } from '@/typings/canvas';
import { useTextShapeState } from '@/composables/useTextShapeState';

type DateFormatConfig = { format?: string; numerals?: 'roman'; separator?: string };


export default /*#__PURE__*/_defineComponent({
  __name: 'TextAdvancedProperties',
  props: {
    canvas: null,
    textElement: null,
    editMode: { type: Boolean },
    exportFormat: null,
    melcoTextIds: null,
    forDesignTemplateCreation: { type: Boolean },
    isTemplateCreation: { type: Boolean }
  },
  emits: ["element-props-updated", "use-as-mask-change", "use-as-cutout-change", "allow-edit-change", "autofill-toggle", "adjust-text-position"],
  setup(__props: any, { emit }: { emit: ({
    (e: 'element-props-updated', value: string): void;
    (e: 'use-as-mask-change', value: boolean): void;
    (e: 'use-as-cutout-change', value: boolean): void;
    (e: 'allow-edit-change'): void;
    (e: 'autofill-toggle'): void;
    (e: 'adjust-text-position'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
    canvas: CanvasEx;
    textElement?: BoundedTextElement | TextTransformElement | PathITextElement;
    editMode?: boolean;
    exportFormat?: string;
    melcoTextIds?: string[];
    forDesignTemplateCreation?: boolean;
    isTemplateCreation?: boolean;
};

const AUTOFILL_OPTIONS: CSelectItem[] = [
    { value: 'ORDER_NUMBER', label: 'Order Number' },
    { value: 'FILENAME', label: 'File Name' },
];

const DATE_FORMAT_OPTIONS: CSelectItem[] = [
    { value: 'MM-DD-YY', label: '10-14-25' },
    { value: 'MM.DD.YYYY', label: '10.14.2025' },
    { value: 'MM.DD.YY', label: '10.14.25' },
    { value: 'DD/MM/YYYY', label: '14/10/2025' },
    { value: 'DD-MM-YY', label: '14-10-25' },
    { value: 'DD.MM.YYYY', label: '14.10.2025' },
    { value: 'DD.MM.YY', label: '14.10.25' },
    { value: 'YYYY-MM-DD', label: '2025-10-14' },
    { value: 'DD MMM YYYY', label: '14 Oct 2025' },
    { value: 'MMM D, YYYY', label: 'Oct 14, 2025' },
    { value: 'YYYY MMM D', label: '2025 Oct 14' },
    { value: 'DD-MMM-YYYY', label: '14-Oct-2025' },
    { value: 'DD MMMM YYYY', label: '14 October 2025' },
    { value: 'MMMM D, YYYY', label: 'October 14, 2025' },
    { value: 'YYYY MMMM D', label: '2025 October 14' },
    { value: 'dddd, MMMM D, YYYY', label: 'Tuesday, October 14, 2025' },
];

const DATE_SEPARATOR_OPTIONS: CSelectItem[] = [
    { value: '·', label: '· (middle dot)' },
    { value: '.', label: '. (dot)' },
    { value: '-', label: '- (dash)' },
    { value: '/', label: '/ (slash)' },
    { value: ' ', label: 'Space' },
];

const DATE_LANG_WARNING_FORMATS = new Set([
    'DD MMM YYYY', 'MMM D, YYYY', 'YYYY MMM D', 'DD-MMM-YYYY',
    'DD MMMM YYYY', 'MMMM D, YYYY', 'YYYY MMMM D', 'dddd, MMMM D, YYYY',
]);





const model = reactive({
    prefix: '',
    suffix: '',
    betweenCharacters: '',
    betweenWords: '',
    referenceId: '',
    allowEditChecked: false,
    useAsMask: false,
    useAsCutout: false,
    autofillSource: '',
    dateFormat: '',
    dateRoman: false,
    dateSeparator: '',
});

const isBoundedText = computed(() => props.textElement?.type === 'bounded-text');
const hasTextPathDraw = computed(
    () => isBoundedText.value && !!(props.textElement as BoundedTextElement)?.textPath
);
// A shape turns "Allow Moving Text" off
const { hasTextShape } = useTextShapeState(
    () => props.textElement,
    (element) => (model.allowEditChecked = element.allowEdit || element.oldAllowEdit || false)
);
const isOFM = computed(() => props.exportFormat === 'ofm');

const melcoOptions = computed<CSelectItem[]>(() =>
    (props.melcoTextIds || []).map((id) => ({ value: id, label: id }))
);

const showDateLangWarning = computed(
    () =>
        !!model.dateFormat &&
        DATE_LANG_WARNING_FORMATS.has(model.dateFormat) &&
        (!model.dateRoman || model.dateFormat.includes('dddd'))
);

const initModel = (element: BoundedTextElement | TextTransformElement | PathITextElement) => {
    const el = element as BaseTextElement;
    model.prefix = el.prefix || '';
    model.suffix = el.suffix || '';
    model.betweenCharacters = el.betweenCharacters || '';
    model.betweenWords = el.betweenWords || '';
    model.referenceId = el.referenceId || '';
    model.allowEditChecked = el.allowEdit || el.oldAllowEdit || false;
    model.autofillSource = el.autofillSource || '';

    if (element.type === 'bounded-text') {
        const bounded = element as BoundedTextElement;
        model.useAsMask = bounded.useAsMask || false;
        model.useAsCutout = bounded.useAsCutout || false;
        const df = bounded.dateFormat as unknown as string | DateFormatConfig;
        const config = df && typeof df === 'object' ? df : {};
        model.dateFormat = df && typeof df === 'object' ? df.format || '' : df || '';
        model.dateRoman = config.numerals === 'roman';
        model.dateSeparator = config.separator ?? '';
    } else {
        model.useAsMask = false;
        model.useAsCutout = false;
        model.dateFormat = '';
        model.dateRoman = false;
        model.dateSeparator = '';
    }
};

watch(
    () => props.textElement,
    (element) => {
        if (!element) return;
        initModel(element);
    },
    { immediate: true }
);

const updateElementProp = (key: string, value: unknown) => {
    const element = props.textElement as BaseTextElement;
    if (!element) return;
    if (props.editMode) {
        element.setEditedProp(key);
    }
    element.set(key as keyof BaseCanvasElement, value);
    if (element.fire) {
        element.fire('modified');
    }
    props.canvas.requestRenderAll();
    emit('element-props-updated', element.uuid as string);
};

const updateDateFormat = () => {
    if (!model.dateFormat) {
        updateElementProp('dateFormat', null);
        return;
    }
    const config: DateFormatConfig = { format: model.dateFormat };
    if (model.dateRoman) {
        config.numerals = 'roman';
        if (model.dateSeparator) config.separator = model.dateSeparator;
    }
    updateElementProp('dateFormat', config);
};

const handleDateRomanChange = (value: boolean) => {
    model.dateRoman = value;
    updateDateFormat();
};

const handleDateSeparatorChange = (value: unknown) => {
    model.dateSeparator = (value as string) || '';
    updateDateFormat();
};

const handleAutofillSourceChange = (value: unknown) => {
    model.autofillSource = value as string;
    updateElementProp('autofillSource', value);
};

const handleReferenceIdChange = (value: unknown) => {
    model.referenceId = value as string;
    updateElementProp('referenceId', value);
};

const handleDateFormatChange = (value: unknown) => {
    model.dateFormat = (value as string) || '';
    updateDateFormat();
};

const handleAllowEditChange = () => {
    const newValue = !model.allowEditChecked;
    model.allowEditChecked = newValue;
    if (newValue) {
        model.autofillSource = '';
    }
    emit('allow-edit-change');
};

const handleUseAsMaskChange = (value: boolean) => {
    model.useAsMask = value;
    if (value) {
        model.useAsCutout = false;
        model.autofillSource = '';
    }
    emit('use-as-mask-change', value);
};

const handleUseAsCutoutChange = (value: boolean) => {
    model.useAsCutout = value;
    if (value) {
        model.useAsMask = false;
        model.autofillSource = '';
    }
    emit('use-as-cutout-change', value);
};

const handleAutofillToggle = () => {
    if (model.autofillSource) {
        model.autofillSource = '';
    } else {
        model.autofillSource = 'ORDER_NUMBER';
        model.useAsMask = false;
        model.useAsCutout = false;
        model.allowEditChecked = false;
    }
    emit('autofill-toggle');
};

return { __sfc: true,AUTOFILL_OPTIONS, DATE_FORMAT_OPTIONS, DATE_SEPARATOR_OPTIONS, DATE_LANG_WARNING_FORMATS, props, emit, model, isBoundedText, hasTextPathDraw, hasTextShape, isOFM, melcoOptions, showDateLangWarning, initModel, updateElementProp, updateDateFormat, handleDateRomanChange, handleDateSeparatorChange, handleAutofillSourceChange, handleReferenceIdChange, handleDateFormatChange, handleAllowEditChange, handleUseAsMaskChange, handleUseAsCutoutChange, handleAutofillToggle, CInput, CCheckbox, CButton, CSelect, valueReducer }
}

})